import React, { Fragment, useState, FC } from 'react';
import classnames from 'classnames';
import { isEqual } from 'lodash';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import { LocationItem } from '../interface/locationItem.interface';
import { RelocationPackage } from './RelocationPackage';
import { TargetLocation } from './TargetLocation';
import { PositionLocation } from './PositionLocation';
import { LocationStateToggleSection } from './LocationStateToggleSection';
import { LocationsProps } from './props';
import './styles.scss';

export const Locations: FC<LocationsProps> = ({ opening, setField }) => {
  const [isTargetPositionLocationError, setIsTargetPositionLocationError] = useState(false);

  const handlePositionLocationChange = (locations?: (LocationItem | undefined)[]): void => {
    const isLocationsEqual = isEqual(opening.positionLocations, opening.targetLocations);

    const shouldUpdateTarget = !opening?.targetLocations?.length || isLocationsEqual;

    setField({
      positionLocations: locations || [],
      ...(shouldUpdateTarget ? { targetLocations: locations } : {}),
    });

    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.SET_POSITION_LOCATIONS,
      payload: {
        'Opening Id': opening.id,
        'Position Location Ids': locations?.map(item => item?.id || item?.placeId),
        'Position Location Names': locations?.map(item => item?.longName),
      },
    });
  };

  const handleTargetLocationChange = (locations?: (LocationItem | undefined)[]): void => {
    setField({ targetLocations: locations || [] });

    setIsTargetPositionLocationError(false);

    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.SET_TARGET_LOCATIONS,
      payload: {
        'Opening Id': opening.id,
        'Position Location Names': locations?.map(item => item?.longName),
        'Target Location Ids': locations?.map(item => item?.id || item?.placeId),
      },
    });
  };

  const handleRemoteChange = (isChecked: boolean): void => {
    setField({ isRemote: isChecked });

    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.SET_REMOTE_WORKING,
      payload: {
        'Opening Id': opening.id,
        'Remote Enabled': isChecked,
      },
    });
  };

  const handleRelocationChange = (isChecked: boolean): void => {
    setField({ isRelocation: isChecked });

    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.SET_RELOCATION_PACKAGES,
      payload: {
        'Opening Id': opening.id,
        'Remote Enabled': isChecked,
      },
    });
  };

  const hasPositionLocations = !!opening.positionLocations?.length;
  const hasTargetLocations = !!opening.targetLocations?.length;
  const isRemote = !!opening.isRemote;
  const isRelocation = !!opening.isRelocation;

  return (
    <Fragment>
      <div
        className={classnames(
          'page-section',
          isTargetPositionLocationError && 'target-location-error',
          (!hasPositionLocations || !hasTargetLocations) && 'page-section-locations--extra-padding',
        )}
      >
        <div className="page-section-locations__container">
          <PositionLocation onLocationSelected={handlePositionLocationChange} opening={opening} />

          <LocationStateToggleSection
            isChecked={isRemote}
            label="Open for remote working"
            onChange={handleRemoteChange}
            subtitle="Do you want to include people who are interested in remote or hybrid work."
            title="Remote work"
          />
        </div>

        {(hasPositionLocations || isRemote) && (
          <div className="page-section-locations__container">
            <TargetLocation
              invalid={isTargetPositionLocationError}
              isFullWidth={!hasPositionLocations}
              onLocationSelected={handleTargetLocationChange}
              opening={opening}
            />

            {hasPositionLocations && (
              <LocationStateToggleSection
                isChecked={isRelocation}
                label="Open for relocation"
                onChange={handleRelocationChange}
                subtitle="Expand your reach by accepting candidates interested in relocation"
                title="Relocation"
              />
            )}
          </div>
        )}
      </div>

      {isRelocation && hasPositionLocations && (
        <RelocationPackage
          opening={opening}
          updateRelocationBenefits={value => setField({ relocationBenefits: value })}
        />
      )}
    </Fragment>
  );
};
